import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Dropzone } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { UPDATE_TYPE_LABELS } from '@/pages/RegulatoryWatch/labels';
import {
  useAdminCreateUpdateService,
  useAdminCreateVersionService,
  useAdminUploadVersionFileService,
} from '@/services/backoffice';
import { cn } from '@/utils/classUtils';
import { VERSION_STATUSES, UPDATE_TYPES } from '@ditto/shared';
import { Ban, Check, Copy, Star } from 'lucide-react';
import { useState, type FC } from 'react';
import { DatePickerInput } from './DatePickerInput';

// ── Status Badge ──

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-orange-100',
  final: 'bg-green-100',
  withdrawn: 'bg-red-100',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  final: 'Final',
  withdrawn: 'Withdrawn',
  unknown: 'Unknown',
};

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const label = STATUS_LABELS[status] ?? status.replace('_', ' ');
  const color = STATUS_COLORS[status] ?? 'bg-neutral-100';
  return (
    <Badge
      className={cn(
        'h-6 border-transparent text-neutral-900 shadow-none',
        color
      )}
    >
      {label}
    </Badge>
  );
};

const StatusBadgeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          {value ? (
            <StatusBadge status={value} />
          ) : (
            <span className="text-sm text-muted-foreground">
              {placeholder ?? 'Select status'}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {VERSION_STATUSES.filter(s => s !== 'unknown').map(s => (
            <button
              key={s}
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === s ? 'opacity-100' : 'opacity-0'
                )}
              />
              <StatusBadge status={s} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Update Type Badge ──

const UpdateTypeBadge: FC<{ updateType: string }> = ({ updateType }) => {
  const label = UPDATE_TYPE_LABELS[updateType] ?? updateType;

  switch (updateType) {
    case 'new':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-green-100 text-neutral-900 shadow-none">
          <Star className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'revision':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-yellow-100 text-neutral-900 shadow-none">
          <Copy className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'withdrawal':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-red-100 text-neutral-900 shadow-none">
          <Ban className="h-3 w-3" />
          {label}
        </Badge>
      );
    case 'status_change':
      return (
        <Badge className="h-6 gap-1 border-transparent bg-blue-100 text-neutral-900 shadow-none">
          <span className="font-bold">&raquo;</span>
          {label}
        </Badge>
      );
    default:
      return (
        <Badge color="purple" className="gap-1">
          {label}
        </Badge>
      );
  }
};

const UpdateTypeBadgeSelect: FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}> = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="cursor-pointer"
        >
          {value ? (
            <UpdateTypeBadge updateType={value} />
          ) : (
            <span className="text-sm text-muted-foreground">
              {placeholder ?? 'Select type'}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <div className="max-h-[280px] overflow-y-auto py-1">
          {UPDATE_TYPES.filter(t => t !== 'unknown').map(t => (
            <button
              key={t}
              onClick={() => {
                onChange(t);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-sm',
                'hover:bg-accent focus-visible:outline-none focus-visible:bg-accent'
              )}
            >
              <Check
                className={cn(
                  'h-4 w-4 shrink-0',
                  value === t ? 'opacity-100' : 'opacity-0'
                )}
              />
              <UpdateTypeBadge updateType={t} />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// ── Steps ──

type Step = 'version' | 'update';

const STEPS: { key: Step; label: string; subtitle: string }[] = [
  { key: 'version', label: 'Version', subtitle: 'Version details' },
  { key: 'update', label: 'Update', subtitle: 'Associated update' },
];

// ── Modal ──

interface CreateVersionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referenceId: string;
}

export const CreateVersionModal: FC<CreateVersionModalProps> = ({
  open,
  onOpenChange,
  referenceId,
}) => {
  const [step, setStep] = useState<Step>('version');

  // Version fields
  const [status, setStatus] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [url, setUrl] = useState('');
  const [versionFile, setVersionFile] = useState<File | null>(null);

  // Update fields
  const [createUpdate, setCreateUpdate] = useState(true);
  const [updateType, setUpdateType] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [shortSummary, setShortSummary] = useState('');
  const [summary, setSummary] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createVersion } = useAdminCreateVersionService();
  const { createUpdate: createUpdateMutation } = useAdminCreateUpdateService();
  const { uploadFile } = useAdminUploadVersionFileService();

  const resetForm = () => {
    setStep('version');
    setStatus('');
    setEffectiveDate('');
    setUrl('');
    setVersionFile(null);
    setCreateUpdate(true);
    setUpdateType('');
    setPublicationDate('');
    setShortSummary('');
    setSummary('');
    setIsSubmitting(false);
  };

  const stepIndex = STEPS.findIndex(s => s.key === step);
  const isVersionValid = !!status;

  const canGoNext = step === 'version' && isVersionValid;

  const handleNext = () => {
    if (step === 'version') setStep('update');
  };

  const handleBack = () => {
    if (step === 'update') setStep('version');
  };

  const handleSubmit = () => {
    if (!isVersionValid) return;
    setIsSubmitting(true);

    createVersion(
      {
        referenceId,
        status,
        effectiveDate: effectiveDate || undefined,
        url: url || undefined,
      },
      {
        onSuccess: versionResult => {
          const verId = versionResult.id;

          const afterFileUpload = () => {
            if (createUpdate && updateType) {
              createUpdateMutation(
                {
                  referenceId,
                  versionId: verId,
                  updateType,
                  shortSummary: shortSummary || undefined,
                  summary: summary || undefined,
                  publicationDate: publicationDate || undefined,
                },
                {
                  onSuccess: () => {
                    setIsSubmitting(false);
                    onOpenChange(false);
                    resetForm();
                  },
                  onError: () => {
                    setIsSubmitting(false);
                  },
                }
              );
            } else {
              setIsSubmitting(false);
              onOpenChange(false);
              resetForm();
            }
          };

          if (versionFile) {
            uploadFile(
              { versionId: verId, file: versionFile },
              {
                onSuccess: () => afterFileUpload(),
                onError: () => {
                  setIsSubmitting(false);
                },
              }
            );
          } else {
            afterFileUpload();
          }
        },
        onError: () => {
          setIsSubmitting(false);
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        onOpenChange(val);
        if (!val) resetForm();
      }}
    >
      <DialogContent className="max-w-3xl min-h-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Version</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Step {stepIndex + 1} of {STEPS.length} : {STEPS[stepIndex].subtitle}
          </p>
        </DialogHeader>

        {/* Step indicator */}
        <div className="border-y py-4 -mx-6 px-6">
          <div className="flex items-center justify-center gap-8">
            {STEPS.map((s, i) => {
              const isCompleted = i < stepIndex;
              const isCurrent = s.key === step;
              return (
                <div key={s.key} className="flex items-center gap-2.5">
                  <div
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium border',
                      isCurrent &&
                        'border-primary bg-primary text-primary-foreground',
                      isCompleted &&
                        'border-primary bg-primary text-primary-foreground',
                      !isCurrent &&
                        !isCompleted &&
                        'border-border text-muted-foreground'
                    )}
                  >
                    {isCompleted ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span
                    className={cn(
                      'text-sm',
                      isCurrent && 'font-medium',
                      !isCurrent && !isCompleted && 'text-muted-foreground'
                    )}
                  >
                    {s.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'version' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div>
                    <StatusBadgeSelect
                      value={status}
                      onChange={setStatus}
                      placeholder="Select status"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Effective Date (optional)</Label>
                  <DatePickerInput
                    value={effectiveDate}
                    onChange={setEffectiveDate}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>URL (optional)</Label>
                <Input
                  placeholder="https://..."
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Document (optional)</Label>
                {versionFile ? (
                  <div className="flex items-center gap-3 rounded-lg border px-4 py-3">
                    <span className="flex-1 text-sm truncate">
                      {versionFile.name}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVersionFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Dropzone
                    onFilesSelected={files => {
                      if (files[0]) setVersionFile(files[0]);
                    }}
                    title="Drop PDF here"
                    subtitle="or click to browse"
                    buttonText="Browse"
                    accept=".pdf,application/pdf"
                    multiple={false}
                    className="min-h-[200px]"
                  />
                )}
              </div>
            </div>
          )}

          {step === 'update' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">
                    Create an associated update
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Create an update that will appear in the feed of users
                    subscribed to this source.
                  </p>
                </div>
                <Switch
                  checked={createUpdate}
                  onCheckedChange={setCreateUpdate}
                />
              </div>

              <div
                className={cn(
                  'space-y-4 transition-opacity',
                  !createUpdate && 'opacity-40 pointer-events-none'
                )}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Update Type</Label>
                    <div>
                      <UpdateTypeBadgeSelect
                        value={updateType}
                        onChange={setUpdateType}
                        placeholder="Select type"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Publication Date (optional)</Label>
                    <DatePickerInput
                      value={publicationDate}
                      onChange={setPublicationDate}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Short Summary (optional)</Label>
                  <Input
                    value={shortSummary}
                    onChange={e => setShortSummary(e.target.value)}
                    placeholder="One-phrase overview (50 chars max)"
                    maxLength={100}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Summary (optional)</Label>
                  <Textarea
                    placeholder="Update summary..."
                    value={summary}
                    onChange={e => setSummary(e.target.value)}
                    className="min-h-[200px]"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <div>
            {stepIndex > 0 && (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {step !== 'update' ? (
              <Button onClick={handleNext} disabled={!canGoNext}>
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={
                  !isVersionValid ||
                  (createUpdate && !updateType) ||
                  isSubmitting
                }
              >
                {isSubmitting ? 'Creating...' : 'Create'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
